import { useCallback } from 'react';
import { extractMetadataFromUrl } from '../utils/metadataExtractor';
import { UrlValidator } from '../utils/urlValidator';

export interface SourceUrl {
  id: string;
  url: string;
  title: string;
  isValid: boolean;
  isProcessing: boolean;
}

type SourceUrlUpdater = SourceUrl[] | ((prev: SourceUrl[]) => SourceUrl[]);

interface UseSourceUrlsOptions {
  onMetadataError?: (error: unknown) => void;
}

const useSourceUrls = (
  sourceUrls: SourceUrl[],
  setSourceUrls: (updater: SourceUrlUpdater) => void,
  options: UseSourceUrlsOptions = {}
) => {
  const { onMetadataError } = options;

  const addSourceUrl = useCallback(() => {
    const newSource: SourceUrl = {
      id: Date.now().toString(),
      url: '',
      title: '',
      isValid: false,
      isProcessing: false
    };

    setSourceUrls(prev => [...prev, newSource]);
  }, [setSourceUrls]);

  const updateSourceUrl = useCallback((id: string, url: string) => {
    setSourceUrls(prev =>
      prev.map(source =>
        source.id === id ? { ...source, url, isValid: false, isProcessing: false } : source
      )
    );
  }, [setSourceUrls]);

  const updateSourceTitle = useCallback((id: string, title: string) => {
    setSourceUrls(prev =>
      prev.map(source =>
        source.id === id ? { ...source, title } : source
      )
    );
  }, [setSourceUrls]);

  const removeSourceUrl = useCallback((id: string) => {
    setSourceUrls(prev => prev.filter(source => source.id !== id));
  }, [setSourceUrls]);

  const validateAndExtractSourceUrl = useCallback(async (id: string, url: string) => {
    if (!url.trim()) return;

    const validation = UrlValidator.validateUrl(url);
    if (!validation.isValid) {
      setSourceUrls(prev =>
        prev.map(source =>
          source.id === id
            ? { ...source, title: '', isValid: false, isProcessing: false }
            : source
        )
      );
      return;
    }

    setSourceUrls(prev =>
      prev.map(source =>
        source.id === id ? { ...source, isProcessing: true } : source
      )
    );

    try {
      const metadata = await extractMetadataFromUrl(url);
      setSourceUrls(prev =>
        prev.map(source =>
          source.id === id
            ? { ...source, title: metadata.title || url, isValid: true, isProcessing: false }
            : source
        )
      );
    } catch (error) {
      onMetadataError?.(error);
      setSourceUrls(prev =>
        prev.map(source =>
          source.id === id
            ? { ...source, title: url, isValid: true, isProcessing: false }
            : source
        )
      );
    }
  }, [onMetadataError, setSourceUrls]);

  return {
    sourceUrls,
    addSourceUrl,
    updateSourceUrl,
    updateSourceTitle,
    removeSourceUrl,
    validateAndExtractSourceUrl
  };
};

export default useSourceUrls;
